import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { catchError, finalize, of } from 'rxjs';

import { AddOnCatalogItem } from '@app/core/models';
import { AccountContextService } from '@app/core/services/account-context.service';
import { AuthStoreService } from '@app/core/services/auth-store.service';
import { AddonsApiService } from './addons-api.service';

export const DECISION_ENGINE_ADDON_CODE = 'MANAGED_PRICELABS';

export const hasDecisionEngineAccess = (catalog: AddOnCatalogItem[], now = Date.now()): boolean => {
  const offer = catalog.find(item => item.code === DECISION_ENGINE_ADDON_CODE);
  if (!offer || !offer.selectedProperties.length || !offer.paidThroughUtc) return false;
  return new Date(offer.paidThroughUtc).getTime() > now &&
    ['Active', 'CancellationScheduled', 'PaidThrough'].includes(offer.commercialStatus ?? '') &&
    ['Active', 'PaidThrough'].includes(offer.entitlementStatus ?? '') &&
    offer.provisioningStatus === 'Active';
};

@Injectable({ providedIn: 'root' })
export class AddonsStoreService {
  private readonly api = inject(AddonsApiService);
  private readonly accounts = inject(AccountContextService);
  private readonly auth = inject(AuthStoreService);
  private readonly catalogState = signal<AddOnCatalogItem[]>([]);
  private readonly catalogAccountId = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private readonly errorState = signal('');
  private requestVersion = 0;

  readonly catalog = computed(() => this.catalogAccountId() === this.accounts.selectedAccountId()
    ? this.catalogState() : []);
  readonly loading = computed(() => this.loadingState());
  readonly error = computed(() => this.errorState());
  readonly decisionEngineEnabled = computed(() => hasDecisionEngineAccess(this.catalog()));

  constructor() {
    effect(() => {
      const accountId = this.accounts.selectedAccountId();
      const role = this.auth.role();
      if (role !== 'owner' || !accountId) {
        untracked(() => this.clear());
        return;
      }
      untracked(() => this.refresh());
    }, { allowSignalWrites: true });
  }

  refresh(): void {
    const accountId = this.accounts.selectedAccountId();
    if (this.auth.role() !== 'owner' || !accountId) {
      this.clear();
      return;
    }
    const requestVersion = ++this.requestVersion;
    this.catalogAccountId.set(accountId);
    this.catalogState.set([]);
    this.loadingState.set(true);
    this.errorState.set('');
    this.api.list(accountId).pipe(
      catchError(() => {
        if (requestVersion === this.requestVersion) this.errorState.set('Unable to load add-ons.');
        return of<AddOnCatalogItem[]>([]);
      }),
      finalize(() => { if (requestVersion === this.requestVersion) this.loadingState.set(false); })
    ).subscribe(items => {
      if (requestVersion === this.requestVersion && accountId === this.accounts.selectedAccountId()) {
        this.catalogState.set(items);
      }
    });
  }

  private clear(): void {
    this.requestVersion++;
    this.catalogAccountId.set(null);
    this.catalogState.set([]);
    this.loadingState.set(false);
    this.errorState.set('');
  }
}
